import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Database, 
  Server, 
  RefreshCw, 
  HardDrive, 
  FolderSync, 
  CheckCircle2, 
  ShieldAlert, 
  Cpu, 
  Activity, 
  Terminal,
  Heart,
  FileCode,
  ArrowRight,
  Info,
  Play,
  Trash2,
  Settings,
  ShieldCheck,
  Eye,
  Copy,
  Lock,
  Mail,
  Globe,
  Wifi,
  Layers,
  Zap,
  TrendingUp,
  AlertTriangle,
  Send,
  EyeOff
} from 'lucide-react';

interface FileItem {
  id: number | string;
  name: string;
  size: string;
  department: string;
  lastSynced: string;
  type: string;
}

interface NexoraXCloudDashboardProps {
  cloudStorageUsed: number;
  workspaceFiles: FileItem[];
  setCloudStorageUsed: React.Dispatch<React.SetStateAction<number>>;
  setWorkspaceFiles: React.Dispatch<React.SetStateAction<FileItem[]>>;
  workspaceCloudLogs: string[];
  setWorkspaceCloudLogs: React.Dispatch<React.SetStateAction<string[]>>;
  isSyncingWorkspace: boolean;
  setIsSyncingWorkspace: (syncing: boolean) => void;
  defaultModule?: 'STORAGE' | 'MAIL' | 'EDGE' | 'TOF_CONSOLE';
}

// Global immutable credentials
const BANK_SORT_CODE = "Verified Payment Configuration";
const BANK_ACCOUNT_NUM = "Secure Billing Provider";
const PRAISE_PREAMBLE = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";

export function NexoraXCloudDashboard({
  cloudStorageUsed,
  workspaceFiles,
  setCloudStorageUsed,
  setWorkspaceFiles,
  workspaceCloudLogs,
  setWorkspaceCloudLogs,
  isSyncingWorkspace,
  setIsSyncingWorkspace,
  defaultModule
}: NexoraXCloudDashboardProps) {

  // Current selected view subnode (Eclipse architecture modules)
  const [activeModule, setActiveModule] = useState<'STORAGE' | 'MAIL' | 'EDGE' | 'TOF_CONSOLE'>(() => {
    if (defaultModule) return defaultModule;
    return (localStorage.getItem('nexorax_cloud_active_module') as any) || 'STORAGE';
  });

  useEffect(() => {
    if (defaultModule) {
      setActiveModule(defaultModule);
    }
  }, [defaultModule]);

  // Action / API usage tracker (1p per action)
  const [runsCount, setRunsCount] = useState<number>(() => {
    const saved = localStorage.getItem('nexorax_cloud_api_runs');
    return saved ? parseInt(saved, 10) : 10842; // standard baseline tracker count
  });

  // Treemap Container Size Monitoring
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 680, height: 230 });
  const [selectedBlockName, setSelectedBlockName] = useState<string | null>(null);
  const [replicationPulse, setReplicationPulse] = useState(true);
  const [lastMirrorTimestamp, setLastMirrorTimestamp] = useState("2026-06-11 11:15:34Z");
  const [integrityScore, setIntegrityScore] = useState<number>(100);

  // Upload speed meter state
  const [uploadSpeed, setUploadSpeed] = useState<number>(314.5); // MB/s representation

  // SHA-256 hash validation logs
  const [shaHash, setShaHash] = useState<string>("e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855");
  const [isHashing, setIsHashing] = useState(false);

  // Mail server logs & metrics
  const [mailLogs, setMailLogs] = useState<string[]>([
    "IMAP Pipeline listener active on ports [143, 993]",
    "DKIM signature keys verified for @nexorax.co.uk",
    "SPF policy parsed successfully: +ip4:192.0.2.44 v=spf1",
  ]);
  const [selectedEmail, setSelectedEmail] = useState<'support' | 'info'>('support');

  // DNS edge records
  const [dnsRecords, setDnsRecords] = useState([
    { type: 'A', name: '@', value: '192.0.2.44', proxy: true, ttl: 'Auto' },
    { type: 'MX', name: '@', value: 'mail.nexoraxdigital.co.uk (Pri: 10)', proxy: false, ttl: '3600' },
    { type: 'CNAME', name: 'www', value: 'nexoraxdigital.co.uk', proxy: true, ttl: 'Auto' },
    { type: 'TXT', name: '@', value: 'v=spf1 ip4:192.0.2.44 -all', proxy: false, ttl: 'Auto' },
  ]);

  // Toast notices
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Local effect saves
  useEffect(() => {
    localStorage.setItem('nexorax_cloud_active_module', activeModule);
  }, [activeModule]);

  useEffect(() => {
    localStorage.setItem('nexorax_cloud_api_runs', String(runsCount));
  }, [runsCount]);

  // Triggering the 1p tariff increase
  const recordApiCall = (actionName: string) => {
    setRunsCount(prev => prev + 1);
    setWorkspaceCloudLogs(prev => [
      PRAISE_PREAMBLE,
      `[TARIFF] "${actionName}" registered: £0.01 added to API_KEY_ENGINE matrix ledger (SLA: 34ms)`,
      ...prev.filter(l => !l.startsWith("In the name of Allah"))
    ]);
  };

  // Pulse effect
  useEffect(() => {
    const pulseInterval = setInterval(() => {
      setReplicationPulse(prev => !prev);
      // simulate realistic floating speed dial variance
      setUploadSpeed(prev => {
        const delta = (Math.random() - 0.5) * 15;
        return parseFloat(Math.min(450, Math.max(120, prev + delta)).toFixed(1));
      });
    }, 2800);
    return () => clearInterval(pulseInterval);
  }, []);

  // Handle Container Resizing
  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width } = entry.contentRect;
        setDimensions({
          width: Math.max(320, width),
          height: 220
        });
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Treemap configs
  const depts = ["Financial Strategy", "Chronos Ledger", "Operations (Staff Coordinator)", "Defensive Operations"];
  const baselines: Record<string, number> = {
    "Financial Strategy": 1800,
    "Chronos Ledger": 1500,
    "Operations (Staff Coordinator)": 1000,
    "Defensive Operations": 320
  };

  const labelBadges: Record<string, string> = {
    "Financial Strategy": "👑 £150,000 Resource resource",
    "Chronos Ledger": "🏛️ Secure Payment Provider Broker Node",
    "Operations (Staff Coordinator)": "🇵🇰 Pakistan remote node sync",
    "Defensive Operations": "🛡️ CloudSync-02 Vault"
  };

  const deptFilesCount: Record<string, number> = {};
  const deptFiles: Record<string, FileItem[]> = {};
  
  depts.forEach(d => {
    deptFiles[d] = [];
    deptFilesCount[d] = 0;
  });
  
  workspaceFiles.forEach(f => {
    if (deptFiles[f.department]) {
      deptFiles[f.department].push(f);
      deptFilesCount[f.department]++;
    } else {
      deptFiles[f.department] = [f];
      deptFilesCount[f.department] = (deptFilesCount[f.department] || 0) + 1;
    }
  });

  let calculatedSum = 0;
  const deptAllocations = depts.map(dept => {
    const base = baselines[dept] || 500;
    const filesCount = deptFilesCount[dept] || 0;
    const diffCount = filesCount - 1;
    const value = Math.max(100, base + diffCount * 31);
    calculatedSum += value;
    return { name: dept, value };
  });

  const contingencyValue = Math.max(0, 10000 - cloudStorageUsed);

  // Construct hierarchy data for D3 Treemap
  const treemapData = {
    name: "NexoraXStorage",
    children: [
      ...deptAllocations.map(d => ({
        name: d.name,
        value: d.value,
        isContingency: false
      })),
      {
        name: "System Contingency",
        value: contingencyValue,
        isContingency: true
      }
    ]
  };

  // Compute treemap geometry layouts
  const root = d3.hierarchy<any>(treemapData)
    .sum((d: any) => d.value)
    .sort((a: any, b: any) => (b.value || 0) - (a.value || 0));

  d3.treemap<any>()
    .size([dimensions.width, dimensions.height])
    .paddingInner(3)
    .paddingOuter(1)
    (root);

  const leaves = root.leaves() as Array<d3.HierarchyRectangularNode<any>>;

  // Force storage snap re-sync
  const handleGuardianForceTrigger = () => {
    setIsSyncingWorkspace(true);
    recordApiCall("Storage Snapshot Sync (CloudSync-02)");
    setWorkspaceCloudLogs(prev => [
      PRAISE_PREAMBLE,
      `[CloudSync-02] Active signal interception caught. Dispatching manual mirroring pass...`,
      `[CloudSync-02] Mounting secure block sectors of Google Drive NexoraX Resource Library...`,
      `[CloudSync-02] Initiating cryptographic handshake on port 3000 verification line...`,
      ...prev.filter(l => !l.startsWith("In the name of Allah"))
    ]);

    setTimeout(() => {
      setIsSyncingWorkspace(false);
      const formatTime = new Date().toISOString().replace("T", " ").substring(0, 19) + "Z";
      setLastMirrorTimestamp(formatTime);
      setIntegrityScore(100);
      setWorkspaceCloudLogs(prev => [
        PRAISE_PREAMBLE,
        `[SUCCESS] CloudSync-02 mirror verified complete. 100% of ${cloudStorageUsed.toLocaleString()} GB integrity validated.`,
        `[VAULT_STORES] Synced mirror point: gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain`,
        ...prev.filter(l => !l.startsWith("In the name of Allah"))
      ]);
      showToast("Storage Mirror Snapshot Secured via Nex Agent.");
    }, 1200);
  };

  // Run dynamic SHA-256 calculation simulator
  const handleCalculateSHA = () => {
    setIsHashing(true);
    recordApiCall("SHA-256 Block Signature Generation");
    setShaHash("Computing signature hash...");

    setTimeout(() => {
      const chars = "abcdef0123456789";
      let res = "";
      for (let i = 0; i < 64; i++) {
        res += chars[Math.floor(Math.random() * chars.length)];
      }
      setShaHash(res);
      setIsHashing(false);
      showToast("SHA-256 Block integrity verified successfully!");
    }, 850);
  };

  // Test dynamic mail pipeline sync
  const handleSendTestMail = () => {
    recordApiCall("Test SMTP/IMAP Outbound Packet Link");
    const targetAddress = selectedEmail === 'support' ? 'support@nexoraxdigital.co.uk' : 'info@nexoraxdigital.co.uk';
    const fakeId = Math.floor(100000 + Math.random() * 900000);
    
    setMailLogs(prev => [
      `[SMTP_ALERT] Dispatching handshake to mail MX servers...`,
      `[SPF_PASS] Documentation IP address 192.0.2.44 matched target policy rules.`,
      `[DKIM_PASS] SHA256-RSA cryptograhic key matches master header block.`,
      `[SUCCESS] Mail envelope registered transit callback queue ID: nex-msg-${fakeId}`,
      ...prev
    ]);
    showToast(`SMTP test envelope dispatched to ${targetAddress}`);
  };

  // Add DNS Record
  const handleAddDnsRecord = () => {
    recordApiCall("Add Route DNS Record Entry");
    const newRec = { type: 'CNAME', name: 'api-stage', value: 'hetzner-bare-metal-stage.nexorax.de', proxy: true, ttl: 'Auto' };
    setDnsRecords(prev => [...prev, newRec]);
    showToast("CNAME Record successfully appended to Cloudflare proxy cluster.");
  };

  // Invalidate cache
  const handlePurgeCache = () => {
    recordApiCall("Cloudflare Global Cache Eviction");
    showToast("Edge proxy cache invalidated instantly across 314 PoPs.");
  };

  const selectedDeptData = selectedBlockName ? {
    name: selectedBlockName,
    value: selectedBlockName === "System Contingency" 
      ? contingencyValue 
      : (deptAllocations.find(d => d.name === selectedBlockName)?.value || 0),
    percentage: Math.round(((selectedBlockName === "System Contingency" 
      ? contingencyValue 
      : (deptAllocations.find(d => d.name === selectedBlockName)?.value || 1)) / 10000) * 100),
    filesCount: selectedBlockName === "System Contingency" ? 0 : (deptFilesCount[selectedBlockName] || 0),
    files: selectedBlockName === "System Contingency" ? [] : (deptFiles[selectedBlockName] || [])
  } : null;

  // Fiscal Calculations based on 1p per request tariff
  const totalGrossRevenue = runsCount * 0.01;
  const NexoraXPerpetualLevy = totalGrossRevenue * 0.01;
  const corporateTaxReserve = totalGrossRevenue * 0.20;
  const netSecurePaymentSettlement = totalGrossRevenue - NexoraXPerpetualLevy - corporateTaxReserve;
  
  const charitySplit1 = NexoraXPerpetualLevy / 3;
  const charitySplit2 = NexoraXPerpetualLevy / 3;
  const charitySplit3 = NexoraXPerpetualLevy / 3;

  return (
    <div id="NexoraX-cloud-visualizer" className="bg-[#040813] border border-white/5 rounded-2xl p-6 shadow-2xl space-y-6 relative overflow-hidden">
      
      {/* 34ms Latency rebrand badge for Nex Agent */}
      <div className="absolute top-2.5 right-6 flex items-center gap-1.5 bg-[#00e5ff]/15 border border-[#00e5ff]/35 px-2.5 py-0.5 rounded text-[8px] font-mono font-bold tracking-widest text-[#00e5ff] uppercase">
        <Wifi className="w-2.5 h-2.5 animate-pulse" />
        NEX AGENT ACTIVE • SLA: 34ms FLOOR
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/5 pb-5">
        <div className="space-y-1 text-left">
          <span className="text-[9px] font-mono font-black text-cyan-400 uppercase tracking-widest block">
            Node-17 NexoraX Cloud Infrastructure Interface
          </span>
          <h2 className="text-lg font-bold text-slate-100 uppercase tracking-wider font-sans flex items-center gap-2">
            <Server size={18} className="text-cyan-400 animate-pulse" />
            NEXORAX CLOUD CONTROL CONSOLE
          </h2>
          <p className="text-[10px] text-slate-400 max-w-xl font-sans tracking-wide">
            Industrial cyber-infrastructure dashboard governing multi-tenant backup blocks, active SMTP enterprise mail queues, and Cloudflare DNS edge tunnels routing directly to bare-metal Hetzner servers.
          </p>
        </div>

        {/* Modular Pill Tab Selectors */}
        <div className="flex items-center bg-[#02050b] border border-white/5 p-1 rounded-lg shrink-0 overflow-x-auto">
          <button
            onClick={() => { setActiveModule('STORAGE'); recordApiCall("View Storage Module"); }}
            className={`flex items-center gap-1 px-2.5 py-1.5 font-mono text-[9px] uppercase font-bold rounded transition-all whitespace-nowrap ${
              activeModule === 'STORAGE' ? 'bg-[#00e5ff]/20 border border-[#00e5ff]/40 text-[#00e5ff] font-extrabold' : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            <Database size={12} />
            Firebase Eclipse (Storage)
          </button>
          <button
            onClick={() => { setActiveModule('MAIL'); recordApiCall("View Mail Ingress Module"); }}
            className={`flex items-center gap-1 px-2.5 py-1.5 font-mono text-[9px] uppercase font-bold rounded transition-all whitespace-nowrap ${
              activeModule === 'MAIL' ? 'bg-[#00e5ff]/20 border border-[#00e5ff]/40 text-[#00e5ff] font-extrabold' : 'text-slate-500 hover:text-slate-350'
            }`}
          >
            <Mail size={12} />
            Exchange Eclipse (Mail)
          </button>
          <button
            onClick={() => { setActiveModule('EDGE'); recordApiCall("View Cloudflare Edge Module"); }}
            className={`flex items-center gap-1 px-2.5 py-1.5 font-mono text-[9px] uppercase font-bold rounded transition-all whitespace-nowrap ${
              activeModule === 'EDGE' ? 'bg-[#00e5ff]/20 border border-[#00e5ff]/40 text-[#00e5ff] font-extrabold' : 'text-slate-500 hover:text-slate-350'
            }`}
          >
            <Globe size={12} />
            Cloudflare Eclipse (Edge Proxy)
          </button>
          <button
            onClick={() => { setActiveModule('TOF_CONSOLE'); recordApiCall("View OpenTofu IaC Module"); }}
            className={`flex items-center gap-1 px-2.5 py-1.5 font-mono text-[9px] uppercase font-bold rounded transition-all whitespace-nowrap ${
              activeModule === 'TOF_CONSOLE' ? 'bg-pink-950/30 border border-pink-500/40 text-pink-400 font-extrabold' : 'text-slate-500 hover:text-slate-305'
            }`}
          >
            <FolderSync size={12} />
            OpenTofu IaS Console
          </button>
        </div>
      </div>

      {/* Floating System-wide Toast Alert */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="absolute top-18 right-6 bg-[#031d25] border border-[#00e5ff]/50 px-3 py-1.5 rounded z-50 text-[10px] font-mono uppercase font-black text-[#00e5ff] flex items-center gap-1.5 shadow-[0_0_15px_rgba(0,229,255,0.15)]"
          >
            <CheckCircle2 size={12} className="text-emerald-400 text-left" />
            {toastMessage}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Primary Context Switcher Workspace */}
      <div className="bg-[#02050b]/80 border border-white/5 rounded-xl p-5 min-h-[360px] flex flex-col justify-between">
        <AnimatePresence mode="wait">
          
          {/* INTERACTION MATRIX A: FIREBASE ECLIPSE STORAGE BLOCK */}
          {activeModule === 'STORAGE' && (
            <motion.div
              key="storage-block"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              <div className="flex flex-col xl:flex-row gap-6">
                
                {/* Visual D3 Treemap & Metrics Area */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 border-b border-white/5 pb-1 select-none">
                    <span className="uppercase text-left font-bold tracking-wider">Multi-tenant Departmental Storage Allocations</span>
                    <span className="text-[#00e5ff] font-extrabold">{cloudStorageUsed.toLocaleString()} GB / 10,000 GB USED</span>
                  </div>

                  {/* SVG Canvas for Treemap */}
                  <div 
                    ref={containerRef} 
                    className="w-full h-[220px] bg-[#02050b] rounded-xl border border-white/5 overflow-hidden shadow-inner cursor-crosshair relative"
                  >
                    {dimensions.width > 0 && dimensions.height > 0 && (
                      <svg width={dimensions.width} height={dimensions.height} className="block">
                        <g>
                          {leaves.map((leaf, index) => {
                            const name = leaf.data.name;
                            const value = leaf.data.value;
                            const isSelected = selectedBlockName === name;
                            const width = Math.max(1, leaf.x1 - leaf.x0);
                            const height = Math.max(1, leaf.y1 - leaf.y0);

                            const fillColor = name === "Financial Strategy" ? "#1e293b" :
                                              name === "Chronos Ledger" ? "#0f172a" :
                                              name === "Operations (Staff Coordinator)" ? "#022c22" :
                                              name === "Defensive Operations" ? "#311021" :
                                              "#090d16";

                            const strokeColor = name === "Financial Strategy" ? "#00c3ff" :
                                               name === "Chronos Ledger" ? "#00e5ff" :
                                               name === "Operations (Staff Coordinator)" ? "#059669" :
                                               name === "Defensive Operations" ? "#dc2626" :
                                               "#475569";

                            return (
                              <g 
                                key={index} 
                                transform={`translate(${leaf.x0}, ${leaf.y0})`}
                                onClick={() => { setSelectedBlockName(isSelected ? null : name); recordApiCall(`Inspect ${name} storage`); }}
                                className="transition-all duration-300"
                              >
                                <rect
                                  width={width}
                                  height={height}
                                  fill={fillColor}
                                  stroke={isSelected ? "#00e5ff" : strokeColor}
                                  strokeWidth={isSelected ? 2 : 1}
                                  strokeOpacity={isSelected ? 0.9 : 0.4}
                                  className="cursor-pointer transition-all hover:brightness-135"
                                  rx={3}
                                />
                                {width > 80 && height > 40 ? (
                                  <foreignObject width={width} height={height} className="pointer-events-none p-1.5 overflow-hidden">
                                    <div className="flex flex-col justify-between h-full items-start text-left select-none">
                                      <span className="font-mono text-[8px] font-black text-slate-400 uppercase tracking-widest">{name.split(" ")[0]}</span>
                                      <span className="font-mono text-[10px] font-black leading-none text-white">{value.toLocaleString()} GB</span>
                                    </div>
                                  </foreignObject>
                                ) : width > 30 ? (
                                  <text x={width / 2} y={height / 2 + 3} textAnchor="middle" fill="#94a3b8" fontSize={8} fontFamily="monospace" fontWeight="bold">
                                    {Math.round((value / 10000) * 100)}%
                                  </text>
                                ) : null}
                              </g>
                            );
                          })}
                        </g>
                      </svg>
                    )}
                  </div>

                  {/* Color category labels */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {depts.map(d => (
                      <button
                        key={d}
                        onClick={() => { setSelectedBlockName(selectedBlockName === d ? null : d); recordApiCall(`Inspect ${d} button`); }}
                        className={`text-[8px] px-2 py-1 rounded font-mono font-bold uppercase transition-all flex items-center gap-1 border ${
                          selectedBlockName === d ? 'bg-[#00e5ff]/20 border-[#00e5ff] text-[#00e5ff]' : 'bg-black/40 border-white/5 text-slate-400'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          d === "Financial Strategy" ? "bg-cyan-400" :
                          d === "Chronos Ledger" ? "bg-cyan-500" :
                          d === "Operations (Staff Coordinator)" ? "bg-emerald-400" : "bg-red-500"
                        }`} />
                        {d.split(" ")[0]} ({(deptAllocations.find(da => da.name === d)?.value || 0).toLocaleString()} GB)
                      </button>
                    ))}
                  </div>
                </div>

                {/* Industrial Upload Dials & Meters */}
                <div className="w-full xl:w-80 flex flex-col gap-4 text-left">
                  
                  {/* Upload speed Dial widget representation */}
                  <div className="bg-[#02050b] border border-white/5 p-4 rounded-xl flex items-center justify-between relative overflow-hidden">
                     <div className="space-y-1">
                       <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-widest block font-bold">REAL-TIME SPEED DIAL</span>
                       <div className="text-xl font-bold font-mono tracking-tight text-white flex items-baseline gap-1">
                         {uploadSpeed}
                         <span className="text-[10px] text-cyan-400 font-normal">MB/s</span>
                       </div>
                       <p className="text-[8px] font-mono text-slate-500">Dual SSD RAID array write buffer threshold</p>
                     </div>
                     {/* Circular gauge replica rendered with SVG */}
                     <div className="h-14 w-14 shrink-0 relative flex items-center justify-center">
                        <svg className="w-14 h-14 transform -rotate-90">
                           <circle cx="28" cy="28" r="24" stroke="#122530" strokeWidth="4" fill="transparent" />
                           <circle cx="28" cy="28" r="24" stroke="#00e5ff" strokeWidth="4" fill="transparent" 
                                   strokeDasharray="150" strokeDashoffset={150 - (uploadSpeed / 450) * 150} strokeLinecap="round" className="transition-all duration-300" />
                        </svg>
                        <Zap className="absolute text-[#00e5ff] w-4 h-4 animate-pulse" />
                     </div>
                  </div>

                  {/* Block File extension distribution meters */}
                  <div className="bg-[#02050b] border border-white/5 p-4 rounded-xl space-y-3">
                     <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-widest block font-bold">BLOCK-EXT BYTE DIST METERS</span>
                     <div className="space-y-2 font-mono text-[9px]">
                        <div>
                           <div className="flex justify-between text-slate-400 text-left mb-1">
                              <span>*.CJS (Compiled Server Bundles)</span>
                              <span>4,210 MB</span>
                           </div>
                           <div className="w-full bg-[#122530] h-1.5 rounded-full overflow-hidden">
                              <div className="bg-[#00e5ff] h-full" style={{ width: '42.1%' }} />
                           </div>
                        </div>
                        <div>
                           <div className="flex justify-between text-slate-400 text-left mb-1">
                              <span>*.TSX (Client UI Code Blocks)</span>
                              <span>2,850 MB</span>
                           </div>
                           <div className="w-full bg-[#122530] h-1.5 rounded-full overflow-hidden">
                              <div className="bg-cyan-500 h-full" style={{ width: '28.5%' }} />
                           </div>
                        </div>
                        <div>
                           <div className="flex justify-between text-slate-400 text-left mb-1">
                              <span>*.JSON (Metadata ledgers & keys)</span>
                              <span>1,900 MB</span>
                           </div>
                           <div className="w-full bg-[#122530] h-1.5 rounded-full overflow-hidden">
                              <div className="bg-[#e840f3] h-full" style={{ width: '19.0%' }} />
                           </div>
                        </div>
                     </div>
                  </div>

                  {/* Gdrive Vault snapshot action panel */}
                  <div className="bg-[#031d25]/20 border border-cyan-500/20 p-4 rounded-xl space-y-2">
                     <span className="text-[8px] font-mono text-cyan-400 uppercase tracking-widest block font-bold">SHA-256 VALIDATION MONITOR</span>
                     <div className="bg-black/60 border border-white/5 p-1.5 rounded font-mono py-2">
                        <span className="text-[8px] text-slate-500 block">SHA-256 Hex Digest Block Root:</span>
                        <span className="text-[8px] text-zinc-300 select-all block truncate uppercase tracking-widest font-extrabold">{shaHash}</span>
                     </div>
                     <button 
                       onClick={handleCalculateSHA}
                       disabled={isHashing}
                       className="w-full py-1.5 bg-[#00e5ff]/15 hover:bg-[#00e5ff]/25 border border-[#00e5ff]/40 text-[#00e5ff] font-mono text-[9px] uppercase font-bold tracking-wider rounded"
                     >
                       {isHashing ? "Hashing block states..." : "Regenerate SHA-256 Hash Check"}
                     </button>
                  </div>

                </div>

              </div>

              {/* Footer action trigger deck for storage snapshot */}
              <div className="flex items-center justify-between bg-black/60 border border-white/5 p-3 rounded-lg flex-col sm:flex-row gap-3">
                 <div className="text-left">
                    <span className="text-[#00e5ff] font-mono text-[9px] uppercase font-bold block">SECURE GOOGLE DRIVE BACKUP VAULT SHUTTLE</span>
                    <p className="text-[8px] font-mono text-slate-500">Synchronized via automatic <strong>CloudSync-02</strong> mirror matrices (SLA: 0.8s)</p>
                 </div>
                 <div className="flex gap-2 w-full sm:w-auto shrink-0">
                    <button
                      onClick={handleGuardianForceTrigger}
                      disabled={isSyncingWorkspace}
                      className="w-full sm:w-auto px-4 py-1.5 bg-gradient-to-r from-cyan-900 to-emerald-900 border border-cyan-500/30 text-white font-mono text-[9px] uppercase font-bold tracking-widest rounded"
                    >
                      {isSyncingWorkspace ? "Computing..." : "Force Backup Snapshot (CloudSync-02)"}
                    </button>
                 </div>
              </div>
            </motion.div>
          )}

          {/* INTERACTION MATRIX B: EXCHANGE ECLIPSE MAIL SERVER INGRESS ARRAY */}
          {activeModule === 'MAIL' && (
            <motion.div
              key="mail-block"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-4 text-left"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                 
                 {/* Live Mail Queue pipeline display */}
                 <div className="lg:col-span-7 space-y-4">
                    <div className="flex justify-between items-center text-[10px] font-mono text-zinc-400 border-b border-white/5 pb-1">
                       <span className="uppercase text-left font-bold tracking-wider">Exchange Ingress Pipelines</span>
                       <span className="text-emerald-400 font-extrabold uppercase">IMAP/SMTP Active (34ms Gateways)</span>
                    </div>

                    {/* Server connection schema illustration */}
                    <div className="bg-[#02050b] p-4 rounded-xl border border-white/5 relative overflow-hidden flex flex-col justify-between h-[180px]">
                       <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none" />
                       
                       <div className="flex justify-between items-center z-10 border-b border-white/5 pb-2">
                          <div>
                             <span className="text-[8px] font-mono text-slate-500 uppercase block font-bold">Corporate Host Link</span>
                             <strong className="text-xs text-white font-mono uppercase tracking-tight">mail.nexoraxdigital.co.uk</strong>
                          </div>
                          <span className="bg-emerald-950/80 text-emerald-400 text-[8px] font-mono px-2 py-0.5 rounded border border-emerald-500/30 uppercase font-black tracking-widest animate-pulse">
                             Pipeline Connection Established
                          </span>
                       </div>

                       {/* Visual flow graph */}
                       <div className="flex justify-around items-center py-4 z-10">
                          <div className="flex flex-col items-center gap-1 bg-[#091512] border border-emerald-500/20 px-3 py-2 rounded-lg text-center w-24">
                             <Cpu className="text-emerald-400 w-5 h-5" />
                             <span className="text-[8px] font-mono text-slate-400 uppercase">SMTP Server</span>
                             <span className="text-[7.5px] font-mono text-slate-500">Port 587/TLS</span>
                          </div>
                          <div className="flex-1 border-t border-dashed border-emerald-500/30 flex justify-center items-center relative mx-2">
                             <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full absolute -top-1 animate-ping" />
                             <ArrowRight className="text-emerald-500 w-3.5 h-3.5 absolute -top-1.5" />
                          </div>
                          <div className="flex flex-col items-center gap-1 bg-[#0a1e1b] border border-emerald-400/40 px-3 py-2 rounded-lg text-center w-28">
                             <Mail className="text-emerald-400 w-5 h-5 animate-pulse" />
                             <span className="text-[8px] font-mono text-slate-200 uppercase font-bold">Mailbox Ingress</span>
                             <span className="text-[7.5px] font-mono text-emerald-400 truncate w-full" title={selectedEmail === 'support' ? 'support@nexorax.co.uk' : 'info@nexorax.co.uk'}>
                                {selectedEmail === 'support' ? '@support' : '@info'}
                             </span>
                          </div>
                       </div>

                       <div className="text-[8px] font-mono text-slate-400 leading-normal border-t border-white/5 pt-2 flex justify-between">
                          <span>DKIM Validation Key: <strong>Verified ✔</strong></span>
                          <span>SPF Policy rules matching: <strong>Verified 192.0.2.44 ✔</strong></span>
                       </div>
                    </div>

                    {/* Senders choosing options widgets */}
                    <div className="flex gap-4 items-center">
                       <span className="text-[9px] font-mono text-slate-500 uppercase font-bold tracking-wider">Focus Target Mailbox Node:</span>
                       <div className="flex gap-2">
                          <button 
                            onClick={() => { setSelectedEmail('support'); recordApiCall("Toggle Mailbox to Support"); }}
                            className={`px-3 py-1 font-mono text-[9px] uppercase font-bold rounded transition-all ${
                               selectedEmail === 'support' 
                                 ? 'bg-[#00e5ff]/20 border border-[#00e5ff]/40 text-[#00e5ff]' 
                                 : 'bg-black/30 text-slate-500 hover:text-slate-350 border border-slate-900'
                            }`}
                          >
                             support@nexoraxdigital.co.uk
                          </button>
                          <button 
                            onClick={() => { setSelectedEmail('info'); recordApiCall("Toggle Mailbox to Info"); }}
                            className={`px-3 py-1 font-mono text-[9px] uppercase font-bold rounded transition-all ${
                               selectedEmail === 'info' 
                                 ? 'bg-[#00e5ff]/20 border border-[#00e5ff]/40 text-[#00e5ff]' 
                                 : 'bg-black/30 text-slate-500 hover:text-slate-350 border border-slate-900'
                            }`}
                          >
                             info@nexoraxdigital.co.uk
                          </button>
                       </div>
                    </div>
                 </div>

                 {/* Cryptographic Badges & Live Activity Streams */}
                 <div className="lg:col-span-12 xl:col-span-5 flex flex-col justify-between gap-4">
                    
                    {/* DKIM Badges verification logs */}
                    <div className="bg-[#02050b] p-4 rounded-xl border border-white/5 space-y-3">
                       <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-widest block font-bold">DKIM/SPF CRYP_BADGES</span>
                       <div className="grid grid-cols-2 gap-2 text-left">
                          <div className="bg-black/55 border border-emerald-500/20 p-2.5 rounded-lg">
                             <div className="flex items-center gap-1 text-[9px] text-emerald-400 font-bold font-mono">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                DKIM ENCRYPTED
                             </div>
                             <span className="text-[7.5px] font-mono text-slate-500 block mt-1 leading-normal">
                                Key check validated. Header cryptographically authenticated by @nexorax.co.uk domain signers.
                             </span>
                          </div>
                          <div className="bg-black/55 border border-emerald-500/20 p-2.5 rounded-lg">
                             <div className="flex items-center gap-1 text-[9px] text-emerald-400 font-bold font-mono">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                SPF VERIFIED
                             </div>
                             <span className="text-[7.5px] font-mono text-slate-500 block mt-1 leading-normal">
                                Sender Policy framework authorizes Hetzner host ip and SecurePayment service hooks to dispatch records.
                             </span>
                          </div>
                       </div>
                    </div>

                    {/* Operational triggers log box */}
                    <div className="bg-[#02050b] p-4 rounded-xl border border-white/5 flex-1 flex flex-col justify-between gap-3 h-[140px]">
                       <span className="text-[8px] font-mono text-zinc-550 uppercase tracking-widest block font-extrabold text-left">Pipeline Activity logs</span>
                       <div className="flex-1 overflow-y-auto max-h-[80px] text-[8.5px] font-mono text-zinc-400 space-y-1 block pr-1">
                          {mailLogs.map((ml, idx) => (
                             <div key={idx} className="flex gap-1">
                                <span className="text-slate-600">[{new Date().toLocaleTimeString()}]</span>
                                <span className={ml.includes("[SUCCESS]") ? "text-emerald-400" : ml.includes("[ERROR]") ? "text-rose-450" : "text-zinc-300"}>{ml}</span>
                             </div>
                          ))}
                       </div>
                       <button
                         onClick={handleSendTestMail}
                         className="w-full py-1.5 bg-gradient-to-r from-emerald-900 to-cyan-900 border border-emerald-500/30 text-emerald-300 font-mono text-[9px] uppercase font-bold tracking-wider rounded"
                       >
                         Send Diagnostic SMTP Packet (Test Connection)
                       </button>
                    </div>

                 </div>

              </div>
            </motion.div>
          )}

          {/* INTERACTION MATRIX C: CLOUDFLARE EDGE DNS & TUNNEL MONITOR */}
          {activeModule === 'EDGE' && (
            <motion.div
              key="edge-block"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-4 text-left"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                 
                 {/* Visual network routing diagram */}
                 <div className="lg:col-span-12 xl:col-span-5 space-y-3">
                    <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-widest block font-bold text-left">Bare-Metal Network Gateway</span>
                    
                    <div className="bg-[#02050b] p-4 rounded-xl border border-white/5 h-[230px] flex flex-col justify-between relative overflow-hidden">
                       <div className="absolute top-0 right-0 w-32 h-32 bg-[#00e5ff]/5 rounded-full blur-2xl pointer-events-none" />
                       <div className="text-[8px] font-mono text-[#00e5ff] uppercase block font-black">
                          Edge Cache Routing Diagram (Cloudflare to Hetzner)
                       </div>

                       {/* Visual path mapping */}
                       <div className="flex flex-col gap-2 relative">
                          <div className="flex items-center gap-3 bg-black/40 border border-white/5 p-1.5 rounded text-left">
                             <div className="w-5 h-5 bg-cyan-950/80 text-[#00e5ff] flex items-center justify-center rounded border border-[#00e5ff]/20">
                                <Zap className="w-3.5 h-3.5" />
                             </div>
                             <div className="font-mono text-[8px] text-slate-350">
                                <span className="block text-[7px] text-slate-500 uppercase font-bold">Inbound Traffic Point</span>
                                CLIENT CALLS • DOCUMENTATION IP: 192.0.2.44
                             </div>
                          </div>

                          <div className="ml-2.5 h-3 border-l border-dashed border-cyan-500/30" />

                          <div className="flex items-center gap-3 bg-black/40 border border-white/5 p-1.5 rounded text-left">
                             <div className="w-5 h-5 bg-orange-950/80 text-orange-400 flex items-center justify-center rounded border border-orange-500/30">
                                <Globe className="w-3.5 h-3.5" />
                             </div>
                             <div className="font-mono text-[8px] text-slate-350">
                                <span className="block text-[7px] text-slate-500 uppercase font-bold">Cloudflare Edge Firewall Proxy</span>
                                FRANKFURT/LONDON CACHE SHIELD • PROTECTED ✔
                             </div>
                          </div>

                          <div className="ml-2.5 h-3 border-l border-dashed border-cyan-500/30" />

                          <div className="flex items-center gap-3 bg-black/40 border border-[#00e5ff]/30 p-1.5 rounded text-left font-bold">
                             <div className="w-5 h-5 bg-blue-950 text-blue-400 flex items-center justify-center rounded border border-blue-500/40">
                                <Server className="w-3.5 h-3.5" />
                             </div>
                             <div className="font-mono text-[8px] text-white">
                                <span className="block text-[7.5px] text-cyan-400 uppercase font-black">DE German Bare-Metal Node</span>
                                HETZNER CONTAINER UNIT • EXCLUSIVE PORT 3000 INGRESS
                             </div>
                          </div>
                       </div>

                       <div className="flex gap-2">
                          <button
                            onClick={handlePurgeCache}
                            className="w-full py-1 bg-orange-950/20 hover:bg-orange-950/45 text-orange-400 border border-orange-500/30 font-mono text-[9px] uppercase font-bold tracking-widest rounded"
                          >
                             Purge Cloudflare Edge Cache
                          </button>
                       </div>
                    </div>
                 </div>

                 {/* DNS Record directory lists */}
                 <div className="lg:col-span-12 xl:col-span-7 space-y-3">
                    <div className="flex justify-between items-center text-[10px] font-mono text-zinc-400 border-b border-white/5 pb-1">
                       <span className="uppercase text-left font-bold tracking-wider">Cloudflare Zone File (nexoraxdigital.co.uk)</span>
                       <span className="text-[#00e5ff] font-extrabold uppercase">Live Proxied DNS Settings</span>
                    </div>

                    <div className="bg-[#02050b] rounded-lg border border-white/5 overflow-hidden font-mono text-[9px]">
                       <div className="bg-black/85 p-2 grid grid-cols-12 text-slate-500 font-extrabold border-b border-white/5 uppercase select-none text-left">
                          <div className="col-span-2">Type</div>
                          <div className="col-span-3">Record Domain</div>
                          <div className="col-span-4">Endpoint Target Value</div>
                          <div className="col-span-2">Cloudflare proxy</div>
                          <div className="col-span-1 text-right">TTL</div>
                       </div>
                       
                       <div className="divide-y divide-white/5 max-h-[160px] overflow-y-auto block">
                          {dnsRecords.map((dns, idx) => (
                             <div key={idx} className="p-2 grid grid-cols-12 text-slate-300 items-center text-left">
                                <div className="col-span-2 text-cyan-400 font-black tracking-wide">{dns.type}</div>
                                <div className="col-span-3 text-slate-350">{dns.name}</div>
                                <div className="col-span-4 text-emerald-400 font-bold truncate pr-2 select-all" title={dns.value}>{dns.value}</div>
                                <div className="col-span-2">
                                   <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider ${
                                      dns.proxy ? "bg-[#00e5ff]/20 text-[#00e5ff] border border-[#00e5ff]/35" : "bg-zinc-950 text-slate-550"
                                   }`}>
                                      {dns.proxy ? "Proxied" : "DNS Only"}
                                   </span>
                                </div>
                                <div className="col-span-1 text-right text-slate-500">{dns.ttl}</div>
                             </div>
                          ))}
                       </div>
                    </div>

                    <button 
                      onClick={handleAddDnsRecord}
                      className="w-full py-1.5 bg-[#00e5ff]/15 hover:bg-[#00e5ff]/25 border border-[#00e5ff]/30 text-[#00e5ff] font-mono text-[9px] uppercase font-bold tracking-wider rounded text-center block"
                    >
                       Add New CNAME Route Record to Zone File
                    </button>
                 </div>

              </div>
            </motion.div>
          )}

          {/* INTERACTION MATRIX D: PRESERVED OPENTOFU PROVISIONING MACHINE */}
          {activeModule === 'TOF_CONSOLE' && (
            <motion.div
              key="tof-block"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
               <NexoraXOpenTofuConsole />
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      <hr className="border-white/5 my-4" />

      {/* CLUSTERING TELEMETRY GRAPHICS HEADER */}
      <h3 className="text-xs font-bold text-slate-150 uppercase tracking-widest font-mono text-left select-none">
        [STORAGE & EMAIL METERING TELEMETRY]
      </h3>

      {/* Dynamic storage and mail usage details */}
      <div id="storage-email-metering" className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left font-mono text-[9px] text-zinc-300">
         <div className="bg-[#02050b] p-3.5 border border-white/5 rounded-xl flex items-center justify-between">
            <div className="space-y-1">
               <span className="text-slate-500 uppercase block font-bold">Active SLA Latency</span>
               <div className="text-sm font-extrabold text-[#00e5ff] flex items-center gap-1">
                  <Cpu className="w-3.5 h-3.5 animate-pulse" />
                  34ms Guaranteed Threshold
               </div>
               <span className="text-slate-600 block text-[8px]">Nex Agent performance verification</span>
            </div>
         </div>
         <div className="bg-[#02050b] p-3.5 border border-white/5 rounded-xl flex items-center justify-between">
            <div className="space-y-1">
               <span className="text-slate-500 uppercase block font-bold">Mail Inbound/Outbound Ratio</span>
               <div className="text-sm font-extrabold text-emerald-400">
                  Daily Delivery SLA: 100.0% Verified
               </div>
               <span className="text-slate-650 block text-[8px]">support@ & info@ DKIM pass indexes</span>
            </div>
         </div>
         <div className="bg-[#02050b] p-3.5 border border-white/5 rounded-xl flex items-center justify-between">
            <div className="space-y-1">
               <span className="text-slate-500 uppercase block font-bold">Cumulative Storage Replicas</span>
               <div className="text-sm font-extrabold text-amber-500">
                  {cloudStorageUsed.toLocaleString()} GB / 10,000 GB
               </div>
               <span className="text-slate-600 block text-[8px]">Mirrored live via CloudSync-02</span>
            </div>
         </div>
      </div>

      <hr className="border-white/5 my-4" />

      {/* MANDATORY THE TIERED EXTRACTION PROTOCOLS HUD */}
      <div id="extraction-accounting-ledgers" className="space-y-3 text-left">
         <div className="flex justify-between items-center">
            <h3 className="text-xs font-bold text-slate-200 uppercase tracking-widest font-mono select-none">
               [FISCAL TARGET ALIGNMENT]
            </h3>
            <span className="bg-emerald-995/60 text-emerald-400 border border-emerald-900/60 text-[8px] font-mono px-2 py-0.5 rounded font-black uppercase tracking-wider">
               TARRIF ENGINE ACTIVE • 1P-PER-USE RULE REGISTERED
            </span>
         </div>

         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-[#02050b] p-3 rounded-lg border border-white/5 font-mono text-left">
               <span className="text-slate-550 block text-[8.5px] uppercase font-bold">Cumulative Gross Harvest</span>
               <strong className="text-base text-zinc-100 block mt-1">£{totalGrossRevenue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
               <span className="text-slate-600 block text-[7.5px] uppercase mt-0.5">Runs Counted: {runsCount} ops</span>
            </div>
            <div className="bg-[#02050b] p-3 rounded-lg border border-white/5 font-mono text-left">
               <span className="text-slate-550 block text-[8.5px] uppercase font-semibold">1% 3% Community Allocation</span>
               <strong className="text-sm text-yellow-400 block mt-1">£{NexoraXPerpetualLevy.toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</strong>
               <div className="text-[7.5px] text-zinc-500 mt-1 flex justify-between">
                  <span>Dunf: 33.33%</span>
                  <span>Gloб: 33.33%</span>
                  <span>UK: 33.33%</span>
               </div>
            </div>
            <div className="bg-[#02050b] p-3 rounded-lg border border-white/5 font-mono text-left">
               <span className="text-slate-550 block text-[8.5px] uppercase font-bold">20% Corporate Tax index</span>
               <strong className="text-sm text-red-400 block mt-1">£{corporateTaxReserve.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
               <span className="text-slate-600 block text-[7.5px] uppercase mt-0.5">Secure 10% Maintenance & Hosting Fee Vault</span>
            </div>
            <div className="bg-[#02050b] p-3 rounded-lg border border-emerald-520/20 font-mono text-left">
               <span className="text-emerald-500/80 block text-[8.5px] uppercase font-extrabold">Net finance Balance</span>
               <strong className="text-base text-emerald-400 block mt-1">£{netSecurePaymentSettlement.toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</strong>
               <span className="text-emerald-500/60 block text-[7.5px] uppercase mt-0.5">SecurePayment Settled: Verified Payment Configuration | Secure Billing Provider</span>
            </div>
         </div>
      </div>

      <hr className="border-white/5 my-4" />

      {/* CLOUD PARITY DEPLOYMENT INDICATOR */}
      <h3 className="text-xs font-bold text-slate-200 uppercase tracking-widest font-mono text-left select-none">
         [VAULT PARITY MIRROR STATUS]
      </h3>
      <div id="vault-parity-grid" className="bg-[#02050b] border border-[#00e5ff]/10 rounded-xl p-4 flex flex-col md:flex-row justify-between items-center text-left text-zinc-300 font-mono text-[9px] gap-4">
         <div className="space-y-1">
            <span className="text-slate-550 uppercase block font-black">Registered corporate entity</span>
            <strong className="text-slate-200 block text-xs">NexoraX Digital Ltd (SC887095)</strong>
            <p className="text-slate-500 text-[8px]">Operating continuously under system templates configured for Lead Director</p>
         </div>

         <div className="flex gap-4 items-center shrink-0">
            <div className="text-right">
               <span className="text-slate-550 uppercase block text-[8px] font-bold">Last GDrive Handshake</span>
               <strong className="text-[#00e5ff] font-extrabold">{lastMirrorTimestamp}</strong>
               <span className="text-emerald-400 block text-[8px]">Integrity Check: {integrityScore}% Perfect State Match</span>
            </div>
            <button
              onClick={handleGuardianForceTrigger}
              className="px-4 py-2 bg-[#00e5ff]/15 hover:bg-[#00e5ff]/25 border border-[#00e5ff]/40 text-[#00e5ff] font-bold uppercase tracking-widest rounded transition-all cursor-pointer text-center"
            >
               Force Parity Sync
            </button>
         </div>
      </div>

    </div>
  );
}

// ==========================================
// CENTRALIZED OPENTOFU PROVISIONING MATRIX
// ==========================================

interface TofuResource {
  id: string;
  type: string;
  name: string;
  status: 'PROVISIONED' | 'DESTROYED' | 'CORRUPTED';
  latency: string;
  ip: string;
}

const PROVIDER_HCL = `# provider.tf
terraform {
  required_providers {
    nexorax = {
      source  = "nexorax/NexoraX-cloud"
      version = "2.4.0"
    }
  }
}

provider "nexorax" {
  director_auth_id   = "SC887095-NexoraX-Admin"
  direct_voice_trunk = "08000 622827"
  latency_sla_ms     = 34
}`;

const MAIN_HCL = `# main.tf
resource "nexorax_node" "vanguard_closer" {
  node_name         = "Vanguard-Closer-Node-01"
  activation_slot   = "priority-weekly-funnel"
  latency_ms_target = 34
  enabled           = true
}

resource "nexorax_ledger" "chronos_ledger" {
  ledger_id         = "Chronos-Ledger-Node-17"
  bank_sort_code    = "Verified Payment Configuration"
  bank_account_num  = "Secure Billing Provider"
  SecurePayment_holding_base = 2227800
}

resource "nexorax_gdrive_vault" "imperial_backup" {
  guardian_model     = "CloudSync-02"
  source_path        = "/var/www/nexorax_source"
  backup_destination = "gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain"
  encryption_method  = "AES256-GCM-HIGH-ENTROPY"
}`;

const VARIABLES_HCL = `# variables.tf
variable "NexoraX_levy_rate" {
  description = "Corporate Perpetual Levy for Charities 1, 2, and 3"
  type        = number
  default     = 0.01 # 1%
}

variable "lifetime_tax_rate" {
  description = "10% Maintenance & Hosting Fee extracted on net balances"
  type        = number
  default     = 0.20 # 20%
}`;

const OUTPUTS_HCL = `# outputs.tf
output "ledger_SecurePayment_balance" {
  value = nexorax_ledger.chronos_ledger.SecurePayment_holding_base
}

output "governing_laws" {
  value = "UK Corporate Regulations & Global Jurisdictions"
}`;

export function NexoraXOpenTofuConsole() {
  const [activeTab, setActiveTab] = useState<'cli' | 'code' | 'state'>('cli');
  const [selectedFile, setSelectedFile] = useState<'main.tf' | 'provider.tf' | 'variables.tf' | 'outputs.tf'>('main.tf');
  const [isInitialized, setIsInitialized] = useState<boolean>(true);
  const [opState, setOpState] = useState<'IDLE' | 'INITING' | 'PLANNING' | 'APPLYING' | 'DESTROYING'>('IDLE');
  
  // Real-time infrastructure resources managed by the Tofu state engine
  const [resources, setResources] = useState<TofuResource[]>([
    { id: "node-vanguard-01", type: "nexorax_node", name: "vanguard_closer", status: 'PROVISIONED', latency: "34ms", ip: "10.0.1.12" },
    { id: "ledger-chronos-17", type: "nexorax_ledger", name: "chronos_ledger", status: 'PROVISIONED', latency: "34ms", ip: "10.0.17.40" },
    { id: "mirror-CloudSync-02", type: "nexorax_gdrive_vault", name: "imperial_backup", status: 'PROVISIONED', latency: "34ms", ip: "10.0.99.15" }
  ]);

  // Terminal log stream
  const [logs, setLogs] = useState<string[]>([
    PRAISE_PREAMBLE,
    "[SYSTEM] NexoraX OpenTofu Provisoning Core - Engine Node-09.2 initialized on Hetzner cluster.",
    "[SYSTEM] State match loaded. Nex Agent core initialized at 34ms SLA floor latency.",
    "Ready for OpenTofu declaratives."
  ]);

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showConsoleToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Helper to highlight HCL code syntactically with complete reliability
  const renderHighlightedHCL = (hcl: string) => {
    const lines = hcl.split('\n');
    return (
      <pre className="text-[10px] font-mono text-slate-350 leading-relaxed overflow-x-auto select-all pr-1 text-left">
        <code>
          {lines.map((line, idx) => {
            if (line.trim().startsWith('#')) {
              return <div key={idx} className="text-slate-500 font-medium">{line}</div>;
            }
            
            // Match resource, provider, variable block lines
            const blockMatch = line.match(/^(\s*)(resource|provider|variable|output|terraform)\s+("([^"]+)"\s+"([^"]+)"|("([^"]+)"))?\s*({)?\s*$/);
            if (blockMatch) {
              const indent = blockMatch[1];
              const keyword = blockMatch[2];
              const name1 = blockMatch[4] || blockMatch[7];
              const name2 = blockMatch[5];
              const hasBrace = blockMatch[8] || (line.endsWith('{') ? '{' : '');
              
              return (
                <div key={idx} className="tracking-wide">
                  {indent}
                  <span className="text-pink-400 font-bold">{keyword}</span>
                  {name1 && <> <span className="text-amber-300">"{name1}"</span></>}
                  {name2 && <> <span className="text-cyan-300">"{name2}"</span></>}
                  {hasBrace && <span className="text-indigo-400"> {hasBrace}</span>}
                </div>
              );
            }

            // Key-Value matches
            const assignmentMatch = line.match(/^(\s*)([a-zA-Z0-9_\-]+)\s*=\s*(.*)$/);
            if (assignmentMatch) {
              const indent = assignmentMatch[1];
              const key = assignmentMatch[2];
              const value = assignmentMatch[3];
              
              let highlightedValue: React.ReactNode = <span className="text-slate-300">{value}</span>;
              if (value.startsWith('"') && value.endsWith('"')) {
                highlightedValue = <span className="text-emerald-400">{value}</span>;
              } else if (value === 'true' || value === 'false') {
                highlightedValue = <span className="text-yellow-400 font-black">{value}</span>;
              } else if (!isNaN(Number(value)) || value.startsWith('#')) {
                highlightedValue = <span className="text-cyan-400 font-bold">{value}</span>;
              }

              return (
                <div key={idx} className="tracking-wide">
                  {indent}
                  <span className="text-sky-400 font-semibold">{key}</span>
                  <span className="text-slate-500"> = </span>
                  {highlightedValue}
                </div>
              );
            }

            return <div key={idx} className="text-slate-300">{line}</div>;
          })}
        </code>
      </pre>
    );
  };

  const getHCLFileContent = () => {
    switch (selectedFile) {
      case 'provider.tf': return PROVIDER_HCL;
      case 'variables.tf': return VARIABLES_HCL;
      case 'outputs.tf': return OUTPUTS_HCL;
      default: return MAIN_HCL;
    }
  };

  const triggerTofuInit = () => {
    setOpState('INITING');
    setLogs(prev => [
      PRAISE_PREAMBLE,
      `[tofu init] INITIALIZING OPEN TOFU PLUGINS ON JURISDICTION: EU-WEST-2...`,
      "[tofu init] Downloading provider \"nexorax/NexoraX-cloud\" v2.4.0...",
      "[tofu init] Verifying checksum credentials for SC887095 authorized provider...",
      "[tofu init] Plugin successfully downloaded. Validated encryption: AES256-GCM.",
      "[tofu font] SUCCESS: OpenTofu NexoraX backend state has been synchronized.",
      "[SUCCESS] OpenTofu initialized! Ready for plan configurations.",
      ...prev.filter(l => !l.startsWith("In the name of Allah"))
    ]);
    setTimeout(() => {
      setIsInitialized(true);
      setOpState('IDLE');
      showConsoleToast("tofu init finalized successfully.");
    }, 1200);
  };

  const triggerTofuPlan = () => {
    if (!isInitialized) {
      setLogs(prev => [
        PRAISE_PREAMBLE,
        "[ERROR] Uninitialized backend state. Please run 'tofu init' before performing calculations.",
        ...prev.filter(l => !l.startsWith("In the name of Allah"))
      ]);
      return;
    }
    setOpState('PLANNING');
    setLogs(prev => [
      PRAISE_PREAMBLE,
      "[tofu plan] Running dry-run check comparing local configurations against state matrices...",
      `[tofu plan] Connecting secure proxy endpoint on SecurePayment Sort Code: Verified Payment Configuration...`
    ]);

    setTimeout(() => {
      const toAdd = resources.filter(r => r.status === 'DESTROYED');
      const toUpdate = resources.filter(r => r.status === 'CORRUPTED');

      const addBlocks = toAdd.map(r => `  + create resource "${r.type}" "${r.name}" {
      + id             = "${r.id}"
      + status         = "PROVISIONED"
      + latency_target = "${r.latency}"
    }`).join('\n\n');

      const updateBlocks = toUpdate.map(r => `  ~ update resource "${r.type}" "${r.name}" {
      ~ status         = "CORRUPTED" => "PROVISIONED"
      ~ self_heal      = "TRIGGERED"
    }`).join('\n\n');

      setLogs(prev => [
        PRAISE_PREAMBLE,
        "[tofu plan] Dynamic assessment finalized successfully. Summary below:",
        "--------------------------------------------------------------------------------",
        toAdd.length > 0 ? addBlocks : "",
        toUpdate.length > 0 ? updateBlocks : "",
        toAdd.length === 0 && toUpdate.length === 0 
          ? "No changes. Infrastructure is public-preview checks passed with standard SLA rules limit configuration." 
          : `Plan: ${toAdd.length} to add, ${toUpdate.length} to change, 0 to destroy.`,
        "--------------------------------------------------------------------------------",
        "[tofu plan] Ready for execution deployment flow. Run 'tofu apply' to commit blocks.",
        ...prev.filter(l => !l.startsWith("In the name of Allah"))
      ]);
      setOpState('IDLE');
      showConsoleToast("tofu plan compiled successfully.");
    }, 1400);
  };

  const triggerTofuApply = () => {
    if (!isInitialized) {
      setLogs(prev => [
        PRAISE_PREAMBLE,
        "[ERROR] Uninitialized backend state. Please run 'tofu init' first.",
        ...prev.filter(l => !l.startsWith("In the name of Allah"))
      ]);
      return;
    }
    setOpState('APPLYING');
    setLogs(prev => [
      PRAISE_PREAMBLE,
      "[tofu apply] Commencing infrastructure build-up sequence...",
      `[tofu apply] Mirroring master configuration blocks to gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain...`
    ]);

    setTimeout(() => {
      setResources(prevRes => prevRes.map(r => ({
        ...r,
        status: 'PROVISIONED' as const
      })));

      setLogs(prev => [
        PRAISE_PREAMBLE,
        "[tofu apply] vanguard_closer (01) deployment verified.",
        "[tofu apply] chronos_ledger (17) database verified. Sort Code links matched.",
        "[tofu apply] imperial_backup replication verified on CloudSync-02 pipeline.",
        "--------------------------------------------------------------------------------",
        "[SUCCESS] Apply complete! All 3/3 target nodes returned to 100% healthy status.",
        "State ledger committed directly to Google Drive NexoraX Resource Library via CloudSync-02.",
        "--------------------------------------------------------------------------------",
        ...prev.filter(l => !l.startsWith("In the name of Allah"))
      ]);
      setOpState('IDLE');
      showConsoleToast("tofu apply completed successfully!");
    }, 1605);
  };

  const triggerTofuDestroy = () => {
    setOpState('DESTROYING');
    setLogs(prev => [
      PRAISE_PREAMBLE,
      "[tofu destroy] Releasing active digital assets and purging state buffers...",
      "[tofu destroy] Warning: This operation cuts routing connections across the Node network."
    ]);

    setTimeout(() => {
      setResources(prevRes => prevRes.map(r => ({
        ...r,
        status: 'DESTROYED' as const
      })));

      setLogs(prev => [
        PRAISE_PREAMBLE,
        "[tofu destroy] vanguard_closer: Node released.",
        "[tofu destroy] chronos_ledger: Node released. SecurePayment reserves unmounted.",
        "[tofu destroy] imperial_backup: Release complete.",
        "--------------------------------------------------------------------------------",
        "[DESTROY COMPLETE] Destroy complete! Resources: 0 added, 0 changed, 3 destroyed.",
        "NexoraX services entered standby contingency mode.",
        "--------------------------------------------------------------------------------",
        ...prev.filter(l => !l.startsWith("In the name of Allah"))
      ]);
      setOpState('IDLE');
      showConsoleToast("tofu destroy completed.");
    }, 1400);
  };

  // Specific single node actions
  const corruptNode = (id: string) => {
    setResources(prev => prev.map(r => r.id === id ? { ...r, status: 'CORRUPTED' as const } : r));
    setLogs(prev => [
      PRAISE_PREAMBLE,
      `[INTRUSION ALERT] Resource "${id}" has been flagged as degraded. Configuration deviation detected.`,
      "[HINT] Run 'tofu plan' and 'tofu apply' to initiate self-healing protocols on the target zone.",
      ...prev.filter(l => !l.startsWith("In the name of Allah"))
    ]);
    showConsoleToast("Infra resource corrupted.");
  };

  const destroyNode = (id: string) => {
    if (window.confirmNexoraXAction) {
      window.confirmNexoraXAction(
        {
          title: `TERMINATE INFRA CONNECTION: ${id.toUpperCase()}`,
          message: `You are about to manually terminate the connection for infrastructure component "${id}". Core disconnect will be simulated, degrading Zone status to DESTROYED. Proceed with caution.`,
          impactLevel: "HIGH",
          nodeName: `Node-09.2 (OpenTofu Cloud)`,
          confirmLabel: "FORCE DISCONNECT",
          cancelLabel: "ABORT DISCONNECT"
        },
        () => {
          setResources(prev => prev.map(r => r.id === id ? { ...r, status: 'DESTROYED' as const } : r));
          setLogs(prev => [
            PRAISE_PREAMBLE,
            `[ADMIN ACTION] Infrastructure component "${id}" manually terminated. Core disconnect simulated.`,
            "[HINT] OpenTofu dynamic plan detects changes. Run build triggers to restore operational SLA.",
            ...prev.filter(l => !l.startsWith("In the name of Allah"))
          ]);
          showConsoleToast("Infra resource destroyed.");
        }
      );
    } else {
      setResources(prev => prev.map(r => r.id === id ? { ...r, status: 'DESTROYED' as const } : r));
      setLogs(prev => [
        PRAISE_PREAMBLE,
        `[ADMIN ACTION] Infrastructure component "${id}" manually terminated. Core disconnect simulated.`,
        "[HINT] OpenTofu dynamic plan detects changes. Run build triggers to restore operational SLA.",
        ...prev.filter(l => !l.startsWith("In the name of Allah"))
      ]);
      showConsoleToast("Infra resource destroyed.");
    }
  };

  const executeCopy = () => {
    navigator.clipboard.writeText(getHCLFileContent());
    showConsoleToast("HCL code copied.");
  };

  return (
    <div className="space-y-4 text-left">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="space-y-1">
          <span className="text-[10px] font-mono font-bold text-pink-400 uppercase tracking-widest block font-sans">
            Infrastructure as Code Matrix (IaS)
          </span>
          <h4 className="text-xs font-black text-slate-100 uppercase tracking-wide flex items-center gap-2">
            <Settings size={14} className="text-pink-400 animate-spin" />
            NexoraX OpenTofu Engine (Node-09.2 Upgrade)
          </h4>
        </div>

        {/* Tab Selection Row */}
        <div className="flex items-center bg-black/45 border border-slate-900 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('cli')}
            className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-[9px] font-black uppercase rounded cursor-pointer transition-all ${
              activeTab === 'cli' ? 'bg-pink-950/40 border border-pink-500/30 text-pink-400' : 'text-slate-500 hover:text-slate-350'
            }`}
          >
            <Terminal size={11} />
            Operations Command
          </button>
          <button
            onClick={() => setActiveTab('code')}
            className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-[9px] font-black uppercase rounded cursor-pointer transition-all ${
              activeTab === 'code' ? 'bg-pink-950/40 border border-pink-500/30 text-pink-400' : 'text-slate-500 hover:text-slate-350'
            }`}
          >
            <FileCode size={11} />
            HCL configurations
          </button>
          <button
            onClick={() => setActiveTab('state')}
            className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-[9px] font-black uppercase rounded cursor-pointer transition-all ${
              activeTab === 'state' ? 'bg-pink-950/40 border border-pink-500/30 text-pink-400' : 'text-slate-500 hover:text-slate-350'
            }`}
          >
            <Database size={11} />
            State Inventory ({resources.filter(r => r.status === 'PROVISIONED').length}/3)
          </button>
        </div>
      </div>

      <div className="bg-[#02050b]/90 border border-slate-900 rounded-xl overflow-hidden shadow-2xl min-h-[280px] flex flex-col justify-between relative">
        <AnimatePresence>
          {toastMessage && (
            <div className="absolute top-3 right-3 bg-pink-950/85 border border-pink-500/60 font-mono text-[9px] uppercase font-bold text-pink-300 px-3 py-1.5 rounded shadow-lg z-50 flex items-center gap-1.5">
              <CheckCircle2 size={10} className="text-pink-400" />
              {toastMessage}
            </div>
          )}
        </AnimatePresence>

        <div className="bg-black/50 border-b border-slate-900/40 p-3 flex justify-between items-center text-[10px] font-mono text-slate-500 select-none">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-rose-500" />
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-slate-400 font-extrabold uppercase ml-2 tracking-wider">
              {activeTab === 'cli' ? 'tofu - CLI execution terminal' :
               activeTab === 'code' ? 'hcl_declarations - config space' :
               'state_inventory.tfstate - registered resources'}
            </span>
          </div>
          <span className="text-pink-404 font-extrabold tracking-widest text-[8px] uppercase">State Controller Loaded</span>
        </div>

        <div className="p-4 flex-1">
          <AnimatePresence mode="wait">
            {activeTab === 'cli' && (
              <motion.div
                key="tab-cli"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-4 font-mono text-[10px]"
              >
                <div className="flex flex-wrap items-center gap-2 border-b border-white/5 pb-3">
                  <button onClick={triggerTofuInit} disabled={opState !== 'IDLE'} className="px-2.5 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-750 text-slate-305 hover:text-white uppercase font-black tracking-wider rounded cursor-pointer disabled:opacity-50 text-[9px]">
                    tofu init
                  </button>
                  <button onClick={triggerTofuPlan} disabled={opState !== 'IDLE'} className="px-2.5 py-1.5 bg-pink-950/20 hover:bg-pink-900/10 border border-pink-500/40 hover:border-pink-500 text-pink-405 uppercase font-black tracking-wider rounded cursor-pointer disabled:opacity-50 text-[9px]">
                    tofu plan
                  </button>
                  <button onClick={triggerTofuApply} disabled={opState !== 'IDLE'} className="px-2.5 py-1.5 bg-emerald-950/20 hover:bg-emerald-900/10 border border-emerald-500/40 hover:border-emerald-500 text-emerald-405 uppercase font-black tracking-wider rounded cursor-pointer disabled:opacity-50 text-[9px]">
                    tofu apply
                  </button>
                  <button onClick={triggerTofuDestroy} disabled={opState !== 'IDLE'} className="px-2.5 py-1.5 bg-rose-950/20 hover:bg-rose-900/10 border border-rose-500/40 hover:border-rose-500 text-rose-455 uppercase font-black tracking-wider rounded cursor-pointer disabled:opacity-50 text-[9px]">
                    tofu destroy
                  </button>

                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-slate-500 uppercase text-[8px] font-bold">STATUS:</span>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[8px] uppercase font-black tracking-widest ${
                      opState === 'IDLE' ? 'bg-emerald-950 text-emerald-400 border border-emerald-900/60' : 'bg-amber-950 text-amber-400 border border-amber-900/60 animate-pulse'
                    }`}>
                      <Activity size={8} />
                      {opState === 'IDLE' ? 'Online Idle' : `RUNNING ${opState}`}
                    </span>
                  </div>
                </div>

                <div className="bg-black/45 rounded-md border border-slate-900 p-3 h-[180px] overflow-y-auto flex flex-col justify-between text-left">
                  <div className="space-y-1 text-slate-350 font-medium leading-relaxed">
                    {logs.map((log, idx) => {
                      let colorClass = "text-slate-300";
                      if (log.startsWith("[SUCCESS]") || log.startsWith("[DESTROY COMPLETE]") || log.includes("complete!")) {
                        colorClass = "text-emerald-400 font-extrabold";
                      } else if (log.startsWith("[ERROR]")) {
                        colorClass = "text-rose-450 font-bold";
                      } else if (log.startsWith("[SYSTEM]")) {
                        colorClass = "text-[#e840f3]";
                      } else if (log.startsWith("[tofu ")) {
                        colorClass = "text-cyan-400 font-bold";
                      } else if (log.startsWith("In the name of Allah")) {
                        colorClass = "text-amber-500/95 font-black block pb-1";
                      }
                      return <div key={idx} className={colorClass}>{log}</div>;
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'code' && (
              <motion.div
                key="tab-code"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-2 border-b border-white/5 pb-2">
                  {(['main.tf', 'provider.tf', 'variables.tf', 'outputs.tf'] as const).map(file => (
                    <button
                      key={file}
                      onClick={() => setSelectedFile(file)}
                      className={`px-3 py-1 font-mono text-[9px] uppercase font-bold rounded cursor-pointer transition-all ${
                        selectedFile === file 
                          ? 'bg-[#00e5ff]/20 border border-[#00e5ff]/35 text-[#00e5ff] font-black' 
                          : 'text-slate-500 hover:text-slate-300'
                      }`}
                    >
                      {file}
                    </button>
                  ))}
                  <button onClick={executeCopy} className="ml-auto flex items-center gap-1 px-2.5 py-1 bg-black/60 hover:bg-slate-900 border border-slate-800 text-slate-400 hover:text-white font-mono text-[8.5px] font-black uppercase tracking-wider rounded">
                    <Copy size={9} />
                    Copy Code
                  </button>
                </div>
                <div className="bg-black/45 rounded-lg border border-slate-900 p-4 h-[180px] overflow-y-auto select-all">
                  {renderHighlightedHCL(getHCLFileContent())}
                </div>
              </motion.div>
            )}

            {activeTab === 'state' && (
              <motion.div
                key="tab-state"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                <div className="text-[9px] font-mono text-slate-500 border-b border-white/5 pb-2 uppercase tracking-wide">
                  Active Virtual resource deployment registry derived from .tfstate tracking ledger
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {resources.map(res => (
                    <div 
                      key={res.id} 
                      className={`bg-black/50 border rounded-xl p-3 text-left space-y-2.5 transition-all relative overflow-hidden ${
                        res.status === 'PROVISIONED' ? 'border-emerald-600/30' :
                        res.status === 'CORRUPTED' ? 'border-amber-600/30' : 'border-rose-600/30'
                      }`}
                    >
                      <span className={`absolute top-2.5 right-2.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[7.5px] uppercase font-black ${
                        res.status === 'PROVISIONED' ? 'bg-emerald-950 text-emerald-400 border border-emerald-900/40' :
                        res.status === 'CORRUPTED' ? 'bg-amber-950 text-amber-400 border border-amber-900/40' :
                        'bg-rose-950 text-rose-400 border border-rose-900/40'
                      }`}>
                        <span className={`w-1 h-1 rounded-full ${res.status === 'PROVISIONED' ? 'bg-emerald-450 animate-pulse' : 'bg-red-500'}`} />
                        {res.status}
                      </span>
                      <div className="space-y-0.5">
                        <span className="text-[7.5px] font-mono font-bold text-pink-400 uppercase tracking-widest block">{res.type}</span>
                        <strong className="text-[10px] font-mono text-slate-200 block truncate">{res.name}</strong>
                      </div>
                      <div className="grid grid-cols-2 gap-1 font-mono text-[8px] text-slate-500 border-t border-white/5 pt-2">
                        <div>
                          <span>SLA LATENCY:</span>
                          <strong className="text-white block font-extrabold">{res.latency}</strong>
                        </div>
                        <div>
                          <span>TARGET IP:</span>
                          <strong className="text-white block font-extrabold">{res.ip}</strong>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 pt-1.5 border-t border-white/5">
                        <button onClick={() => corruptNode(res.id)} disabled={res.status === 'DESTROYED' || res.status === 'CORRUPTED'} className="px-1.5 py-1 bg-black/40 hover:bg-amber-950/25 border border-slate-900 hover:border-amber-500/30 text-slate-500 hover:text-amber-400 font-mono text-[7px] uppercase font-bold rounded cursor-pointer transition-all disabled:opacity-40">
                          Corrupt Node
                        </button>
                        <button onClick={() => destroyNode(res.id)} disabled={res.status === 'DESTROYED'} className="px-1.5 py-1 bg-black/40 hover:bg-rose-950/25 border border-slate-900 hover:border-rose-500/30 text-slate-500 hover:text-rose-400 font-mono text-[7px] uppercase font-bold rounded cursor-pointer transition-all disabled:opacity-40 ml-auto">
                          Destroy Node
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
